/**
 * Werken bij application form.
 *
 * Mirrors the submit contract the rest of this theme already uses, so Make
 * receives the same shape it does from the contact and appointment forms:
 * excluded fields are sent as-is, everything else is merged into
 * contact[fields] as a JSON array of {label, value}, the phone number is
 * normalised by the theme's global formatDutchPhoneNumber, and the payload
 * goes out as FormData.
 *
 * This script owns three things the markup cannot: drag and drop onto the
 * dropzone, the 10 MB size check, and the success and error states.
 */
(function () {
  'use strict';

  // Sent verbatim rather than merged into contact[fields], matching the
  // excluded list the theme's other forms use.
  var EXCLUDED = ['type', 'url', 'mail_add', 'name', 'email', 'phone', 'file'];

  function strings(form) {
    // Every visitor-facing string comes from the locale files via a JSON block
    // in the markup, so this script holds no Dutch copy of its own.
    var box = form.querySelector('[data-vacancy-form-strings]');
    if (!box) return {};
    try {
      return JSON.parse(box.textContent);
    } catch (e) {
      return {};
    }
  }

  function clearMessages(form) {
    var existing = form.querySelectorAll('.vacancy-form__message');
    for (var i = 0; i < existing.length; i++) existing[i].remove();
  }

  function showError(form, title, text) {
    clearMessages(form);
    var wrap = document.createElement('div');
    wrap.className = 'vacancy-form__message rounded-[5px] bg-accent-red p-[16px] text-neutral-white';
    var heading = document.createElement('p');
    heading.className = 'text-body1 font-bold';
    heading.textContent = title;
    wrap.appendChild(heading);
    if (text) {
      var body = document.createElement('p');
      body.className = 'text-body1 font-regular';
      body.textContent = text;
      wrap.appendChild(body);
    }
    form.insertBefore(wrap, form.firstChild);
    wrap.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  function showSuccess(form, title, text) {
    var wrap = document.createElement('div');
    wrap.className = 'vacancy-form__message flex flex-col gap-[7px] rounded-[5px] bg-primary-blue p-[24px] text-neutral-white';
    var heading = document.createElement('p');
    heading.className = 'text-[24px] leading-[32px] font-bold';
    heading.textContent = title;
    var body = document.createElement('p');
    body.className = 'text-body1 font-regular';
    body.textContent = text;
    wrap.appendChild(heading);
    wrap.appendChild(body);

    form.innerHTML = '';
    form.appendChild(wrap);
  }

  function bindDropzone(form, s) {
    var input = form.querySelector('[data-vacancy-file]');
    var zone = form.querySelector('[data-vacancy-dropzone]');
    var label = form.querySelector('[data-vacancy-file-label]');
    if (!input || !zone || !label) return;

    var defaultLabel = label.innerHTML;
    var maxBytes = parseInt(form.getAttribute('data-max-bytes'), 10) || 10485760;

    function reject(message) {
      input.value = '';
      label.innerHTML = defaultLabel;
      showError(form, message, '');
    }

    function accept(file) {
      if (!file) {
        label.innerHTML = defaultLabel;
        return;
      }
      if (file.size > maxBytes) {
        reject(s.file_too_large || 'Dit bestand is te groot.');
        return;
      }
      clearMessages(form);
      label.textContent = file.name;
    }

    input.addEventListener('change', function () {
      accept(input.files && input.files[0]);
    });

    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(function (name) {
      zone.addEventListener(name, function (event) {
        event.preventDefault();
        event.stopPropagation();
      });
    });

    ['dragenter', 'dragover'].forEach(function (name) {
      zone.addEventListener(name, function () {
        zone.classList.add('vacancy-form__dropzone--over');
      });
    });

    ['dragleave', 'drop'].forEach(function (name) {
      zone.addEventListener(name, function () {
        zone.classList.remove('vacancy-form__dropzone--over');
      });
    });

    zone.addEventListener('drop', function (event) {
      var files = event.dataTransfer && event.dataTransfer.files;
      if (!files || !files.length) return;

      // DataTransfer assignment is what makes the dropped file part of the
      // form, so the input stays the single source of truth on submit.
      try {
        var transfer = new DataTransfer();
        transfer.items.add(files[0]);
        input.files = transfer.files;
      } catch (e) {
        // Older browsers: fall back to validating and sending the file
        // straight from the drop event.
        form.vacancyDroppedFile = files[0];
      }

      accept(files[0]);
    });
  }

  function buildPayload(form) {
    var formData = new FormData(form);
    var processed = new FormData();
    var merged = [];

    var phoneInput = form.querySelector('input[name="contact[phone]"]');
    if (phoneInput && phoneInput.value && typeof window.formatDutchPhoneNumber === 'function') {
      formData.set('contact[phone]', window.formatDutchPhoneNumber(phoneInput.value));
    }

    EXCLUDED.forEach(function (field) {
      var value = formData.get('contact[' + field + ']');
      if (value && field !== 'file') {
        processed.append('contact[' + field + ']', value);
      }
    });

    for (var pair of formData.entries()) {
      var match = pair[0].match(/contact\[(.*?)\]/);
      var name = match && match[1];
      if (name && EXCLUDED.indexOf(name) === -1) {
        merged.push({ label: name, value: pair[1] });
      }
    }

    processed.append('contact[fields]', JSON.stringify(merged));

    var input = form.querySelector('[data-vacancy-file]');
    var file = (input && input.files && input.files[0]) || form.vacancyDroppedFile;
    if (file) {
      processed.append('contact[file][0]', file);
    }

    return processed;
  }

  function bindSubmit(form, s) {
    form.addEventListener('submit', async function (event) {
      event.preventDefault();

      var button = form.querySelector('[data-vacancy-submit]');
      var label = form.querySelector('[data-vacancy-submit-label]');
      var endpoint = form.getAttribute('data-endpoint');

      clearMessages(form);

      // Native validation is off on the form so the messages match the rest of
      // the area, but the constraints still hold.
      if (!form.checkValidity()) {
        showError(form, s.invalid_title || 'Niet alle velden zijn ingevuld.', s.invalid_text || '');
        var firstInvalid = form.querySelector(':invalid');
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      if (!endpoint) {
        showError(form, s.error_title || 'Er is iets misgegaan', s.no_endpoint || '');
        return;
      }

      var original = label ? label.textContent : '';
      if (button) button.disabled = true;
      if (label) label.textContent = s.sending || 'Verzenden...';

      try {
        var response = await fetch(endpoint, { method: 'POST', body: buildPayload(form) });
        if (!response.ok) throw new Error('Network response was not ok');
        showSuccess(form, s.success_title || 'Bedankt voor je sollicitatie!', s.success_text || '');
      } catch (error) {
        showError(form, s.error_title || 'Er is iets misgegaan', s.error_text || '');
        if (button) button.disabled = false;
        if (label) label.textContent = original;
      }
    });
  }

  function init(form) {
    if (form.dataset.vacancyFormReady === 'true') return;
    form.dataset.vacancyFormReady = 'true';

    var s = strings(form);
    bindDropzone(form, s);
    bindSubmit(form, s);
  }

  function initAll() {
    var forms = document.querySelectorAll('[data-vacancy-form]');
    for (var i = 0; i < forms.length; i++) init(forms[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
